import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { Box, CircularProgress, Fade } from '@mui/material';
import Sidebar from './components/layout/Sidebar';
import TopBar from './components/layout/TopBar';
import ErrorBoundary from './components/common/ErrorBoundary';
import GlobalAIChat from './components/ai/GlobalAIChat';
import CommandPalette from './components/common/CommandPalette';
import GlobalSearchDialog from './components/common/GlobalSearchDialog';
import KeyboardShortcutsDialog from './components/common/KeyboardShortcutsDialog';
import NotificationCenter from './components/common/NotificationCenter';
import { NotificationProvider } from './contexts/NotificationContext';

// Lazy load pages for better performance
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Jobs = lazy(() => import('./pages/Jobs'));
const Clients = lazy(() => import('./pages/Clients'));
const Estimates = lazy(() => import('./pages/Estimates'));
const SmartSheets = lazy(() => import('./pages/SmartSheets'));
const SmartSheetRunner = lazy(() => import('./pages/SmartSheetRunner'));
const SmartSheetBuilder = lazy(() => import('./pages/SmartSheetBuilder'));
const Permits = lazy(() => import('./pages/Permits'));
const Production = lazy(() => import('./pages/Production'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Reports = lazy(() => import('./pages/Reports'));
const Settings = lazy(() => import('./pages/Settings'));
const CommandCenter = lazy(() => import('./pages/CommandCenter'));
const DesignPipeline = lazy(() => import('./pages/DesignPipeline'));
const CRM = lazy(() => import('./pages/CRM'));
const Staff = lazy(() => import('./pages/Staff'));
const InstallCalendar = lazy(() => import('./pages/InstallCalendar'));
const Forecasting = lazy(() => import('./pages/Forecasting'));
const CustomerOrders = lazy(() => import('./pages/CustomerOrders'));
const ShippingReceiving = lazy(() => import('./pages/ShippingReceiving'));
const OHIEngine = lazy(() => import('./pages/OHIEngine'));

// AI Tools
const AICommand = lazy(() => import('./pages/AICommand'));
const WorkflowMonitor = lazy(() => import('./pages/WorkflowMonitor'));
const ConceptGenerator = lazy(() => import('./pages/ConceptGenerator'));
const CADAssistant = lazy(() => import('./pages/CADAssistant'));
const DraftingCopilot = lazy(() => import('./pages/DraftingCopilot'));
const Fusion360Assistant = lazy(() => import('./pages/Fusion360Assistant'));
const EstimatingAssistant = lazy(() => import('./pages/EstimatingAssistant'));
const InventoryAssistant = lazy(() => import('./pages/InventoryAssistant'));
const PurchasingAssistant = lazy(() => import('./pages/PurchasingAssistant'));
const RevitAssistant = lazy(() => import('./pages/RevitAssistant'));
const ShopDrawingAssistant = lazy(() => import('./pages/ShopDrawingAssistant'));
const PmAssistant = lazy(() => import('./pages/PmAssistant'));

// Additional Pages
const Contacts = lazy(() => import('./pages/Contacts'));
const Opportunities = lazy(() => import('./pages/Opportunities'));
const Vendors = lazy(() => import('./pages/Vendors'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Documents = lazy(() => import('./pages/Documents'));
const Drivers = lazy(() => import('./pages/Drivers'));
const Vehicles = lazy(() => import('./pages/Vehicles'));
const TimeTracking = lazy(() => import('./pages/TimeTracking'));
const Departments = lazy(() => import('./pages/Departments'));
const Tasks = lazy(() => import('./pages/Tasks'));
const Activities = lazy(() => import('./pages/Activities'));
const Tickets = lazy(() => import('./pages/Tickets'));
const CallCenter = lazy(() => import('./pages/CallCenter'));
const Automations = lazy(() => import('./pages/Automations'));
const RoleManagement = lazy(() => import('./pages/RoleManagement'));
const PurchaseOrders = lazy(() => import('./pages/PurchaseOrders'));
const SalesOrders = lazy(() => import('./pages/SalesOrders'));
const Projects = lazy(() => import('./pages/Projects'));
const ActiveRoutes = lazy(() => import('./pages/ActiveRoutes'));
const DesignJobs = lazy(() => import('./pages/DesignJobs'));
const DesignCalendar = lazy(() => import('./pages/DesignCalendar'));
const DesignOrders = lazy(() => import('./pages/DesignOrders'));

// New Pages - Job/Client/Permit Types & Details
const JobTypes = lazy(() => import('./pages/JobTypes'));
const ClientTypes = lazy(() => import('./pages/ClientTypes'));
const ClientDetail = lazy(() => import('./pages/ClientDetail'));
const PermitList = lazy(() => import('./pages/PermitList'));
const PermitTypes = lazy(() => import('./pages/PermitTypes'));
const PermitDetail = lazy(() => import('./pages/PermitDetail'));
const Accounts = lazy(() => import('./pages/Accounts'));
const TeamMembers = lazy(() => import('./pages/TeamMembers'));

// Workflow Pages
const WorkflowPM = lazy(() => import('./pages/WorkflowPM'));

// Inventory Pages
const InventoryCategory = lazy(() => import('./pages/InventoryCategory'));
const InventorySubcategory = lazy(() => import('./pages/InventorySubcategory'));
const InventoryAdjustment = lazy(() => import('./pages/InventoryAdjustment'));
const InventoryAudit = lazy(() => import('./pages/InventoryAudit'));
const InventoryFormula = lazy(() => import('./pages/InventoryFormula'));
const InventoryMaterials = lazy(() => import('./pages/InventoryMaterials'));
const InventoryServices = lazy(() => import('./pages/InventoryServices'));
const InventoryCatalog = lazy(() => import('./pages/InventoryCatalog'));

// Job Detail
const JobDetail = lazy(() => import('./pages/JobDetail'));

// Client Pages
const ClientUserRoles = lazy(() => import('./pages/ClientUserRoles'));

// OHI Pages
const OHIAlerts = lazy(() => import('./pages/OHIAlerts'));
const OHIAudit = lazy(() => import('./pages/OHIAudit'));
const OHICommand = lazy(() => import('./pages/OHICommand'));
const OHIDashboard = lazy(() => import('./pages/OHIDashboard'));
const OHIDivisions = lazy(() => import('./pages/OHIDivisions'));
const OHIDepartments = lazy(() => import('./pages/OHIDepartments'));
const OHIOverview = lazy(() => import('./pages/OHIOverview'));
const OHIReports = lazy(() => import('./pages/OHIReports'));
const OHISettings = lazy(() => import('./pages/OHISettings'));
const OHITeams = lazy(() => import('./pages/OHITeams'));
const OHIUsers = lazy(() => import('./pages/OHIUsers'));

// Production Pages
const ProductionJobs = lazy(() => import('./pages/ProductionJobs'));
const ProductionJobDetail = lazy(() => import('./pages/ProductionJobDetail'));
const ProductionJobType = lazy(() => import('./pages/ProductionJobType'));
const ProductionJobClass = lazy(() => import('./pages/ProductionJobClass'));
const ProductionNoChargeType = lazy(() => import('./pages/ProductionNoChargeType'));
const ProductionSplitJobs = lazy(() => import('./pages/ProductionSplitJobs'));
const ProductionSplitJobDetail = lazy(() => import('./pages/ProductionSplitJobDetail'));
const TmReview = lazy(() => import('./pages/TmReview'));

// Report Pages
const ReportsDashboard = lazy(() => import('./pages/ReportsDashboard'));
const ReportSalesByClient = lazy(() => import('./pages/ReportSalesByClient'));
const ReportSalesByType = lazy(() => import('./pages/ReportSalesByType'));
const ReportProjectStatus = lazy(() => import('./pages/ReportProjectStatus'));
const ReportProductionEfficiency = lazy(() => import('./pages/ReportProductionEfficiency'));
const ReportPermitTracking = lazy(() => import('./pages/ReportPermitTracking'));
const ReportProfitSalesperson = lazy(() => import('./pages/ReportProfitSalesperson'));
const AIAnalyticsDashboard = lazy(() => import('./pages/AIAnalyticsDashboard'));

// Workflow Pages
const WorkflowService = lazy(() => import('./pages/WorkflowService'));
const WorkflowQuestionnaire = lazy(() => import('./pages/WorkflowQuestionnaire'));
const WorkflowCustomField = lazy(() => import('./pages/WorkflowCustomField'));
const WorkflowAgreements = lazy(() => import('./pages/WorkflowAgreements'));
const WorkflowProduction = lazy(() => import('./pages/WorkflowProduction'));
const PMWorkflows = lazy(() => import('./pages/PMWorkflows'));
const AIWorkflowBuilder = lazy(() => import('./pages/AIWorkflowBuilder'));

// Vendor Pages
const VendorTypes = lazy(() => import('./pages/VendorTypes'));
const VendorDetail = lazy(() => import('./pages/VendorDetail'));
const VendorMap = lazy(() => import('./pages/VendorMap'));

// Staff Pages
const StaffScheduler = lazy(() => import('./pages/StaffScheduler'));
const StaffPayout = lazy(() => import('./pages/StaffPayout'));

// Staff Scheduling Pages (AI Scheduler)
const ScheduleBoard = lazy(() => import('./components/scheduler/ScheduleBoard'));
const ScheduleProposals = lazy(() => import('./pages/ScheduleProposals'));
const ProductionSchedule = lazy(() => import('./pages/ProductionSchedule'));
const InstallSchedule = lazy(() => import('./pages/InstallSchedule'));
const UserRoles = lazy(() => import('./pages/UserRoles'));
const SkillsServices = lazy(() => import('./pages/SkillsServices'));

// Purchasing Pages
const PurchasingVendors = lazy(() => import('./pages/PurchasingVendors'));
const PurchasingProposals = lazy(() => import('./pages/PurchasingProposals'));

// Permit Pages
const PermitDashboard = lazy(() => import('./pages/PermitDashboard'));
const PermitNew = lazy(() => import('./pages/PermitNew'));
const PermitWorkflows = lazy(() => import('./pages/PermitWorkflows'));
const AutonomousPermitDashboard = lazy(() => import('./pages/AutonomousPermitDashboard'));
const PermitSettingsPage = lazy(() => import('./pages/PermitSettingsPage'));
const PermitAgentDashboard = lazy(() => import('./pages/PermitAgentDashboard'));

// Misc Pages
const Shipping = lazy(() => import('./pages/Shipping'));
const TaxCategories = lazy(() => import('./pages/TaxCategories'));
const N8NSetup = lazy(() => import('./pages/N8NSetup'));
const AICommandCenter = lazy(() => import('./pages/AICommandCenter'));
const Pickups = lazy(() => import('./pages/Pickups'));
const MonumentEstimate = lazy(() => import('./pages/MonumentEstimate'));
const InstallTemplate = lazy(() => import('./pages/InstallTemplate'));
const DraftingPlan = lazy(() => import('./pages/DraftingPlan'));
const SignSchematic = lazy(() => import('./pages/SignSchematic'));
const SplitSalesOrders = lazy(() => import('./pages/SplitSalesOrders'));
const SalesOrderPipeline = lazy(() => import('./pages/SalesOrderPipeline'));
const ProductionPipeline = lazy(() => import('./pages/ProductionPipeline'));
const InstallDashboard = lazy(() => import('./pages/InstallDashboard'));
const Transportation = lazy(() => import('./pages/Transportation'));

// RBAC / Approval Pages
const ApprovalQueue = lazy(() => import('./pages/ApprovalQueue'));

// Task Queue
const TaskQueue = lazy(() => import('./pages/TaskQueue'));

// World-Class PM Features
const ProjectCopilot = lazy(() => import('./pages/ProjectCopilot'));
const PmDashboard = lazy(() => import('./pages/PmDashboard'));
const ResourcePlanner = lazy(() => import('./pages/ResourcePlanner'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const CollaborationHub = lazy(() => import('./pages/CollaborationHub'));
const DocumentGenerator = lazy(() => import('./pages/DocumentGenerator'));
const ClientPortal = lazy(() => import('./pages/ClientPortal'));
const WorkflowAutomation = lazy(() => import('./pages/WorkflowAutomation'));
const FinancialIntelligence = lazy(() => import('./pages/FinancialIntelligence'));
const RiskDashboard = lazy(() => import('./pages/RiskDashboard'));
const TimeIntelligence = lazy(() => import('./pages/TimeIntelligence'));
const FieldOperations = lazy(() => import('./pages/FieldOperations'));

// Shipping & Logistics
const TruckLoadOptimizer = lazy(() => import('./pages/TruckLoadOptimizer'));

// Loading component
const PageLoader = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100%',
      minHeight: 400,
      flexDirection: 'column',
      gap: 2,
    }}
  >
    <Box
      sx={{
        width: 60,
        height: 60,
        borderRadius: '16px',
        background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'pulse 2s ease-in-out infinite',
        '@keyframes pulse': {
          '0%, 100%': { transform: 'scale(1)', opacity: 1 },
          '50%': { transform: 'scale(0.95)', opacity: 0.8 },
        },
      }}
    >
      <CircularProgress size={30} sx={{ color: 'white' }} />
    </Box>
  </Box>
);

function App() {
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedJob, setSelectedJob] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((event) => {
    // Command Palette: Cmd+K / Ctrl+K
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      setCommandPaletteOpen(prev => !prev);
    }
    // Global Search: Cmd+/ / Ctrl+/
    if ((event.metaKey || event.ctrlKey) && event.key === '/') {
      event.preventDefault();
      setSearchOpen(prev => !prev);
    }
    // Keyboard Shortcuts: Cmd+? / Ctrl+?
    if ((event.metaKey || event.ctrlKey) && event.key === '?') {
      event.preventDefault();
      setShortcutsOpen(prev => !prev);
    }
    // Quick navigation: Cmd+1-4
    if ((event.metaKey || event.ctrlKey) && ['1', '2', '3', '4'].includes(event.key)) {
      event.preventDefault();
      const views = ['dashboard', 'jobs', 'clients', 'estimates'];
      setCurrentView(views[parseInt(event.key) - 1]);
    }
    // Open AI Chat: Cmd+J
    if ((event.metaKey || event.ctrlKey) && event.key === 'j') {
      event.preventDefault();
      // AI Chat is handled by GlobalAIChat component
    }
    // Escape to close
    if (event.key === 'Escape') {
      setCommandPaletteOpen(false);
      setSearchOpen(false);
      setShortcutsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const renderPage = () => {
    switch (currentView) {
      // Dashboard & Command Center
      case 'dashboard':
        return <Dashboard onViewChange={setCurrentView} onOpenCommandPalette={() => setCommandPaletteOpen(true)} onSelectJob={setSelectedJob} />;
      case 'command-center':
        return <CommandCenter onViewChange={setCurrentView} />;
      
      // AI Tools
      case 'ai-command':
        return <AICommand />;
      case 'workflow-monitor':
        return <WorkflowMonitor />;
      case 'concept-generator':
        return <ConceptGenerator />;
      case 'cad-assistant':
        return <CADAssistant />;
      case 'drafting-copilot':
        return <DraftingCopilot />;
      case 'fusion360-assistant':
        return <Fusion360Assistant />;
      case 'estimating-assistant':
        return <EstimatingAssistant />;
      case 'inventory-assistant':
        return <InventoryAssistant />;
      case 'purchasing-assistant':
        return <PurchasingAssistant />;
      case 'revit-assistant':
        return <RevitAssistant />;
      case 'shop-drawing-assistant':
        return <ShopDrawingAssistant />;
      
      // Design
      case 'design-pipeline':
        return <DesignPipeline />;
      case 'design-jobs':
        return <DesignJobs />;
      case 'design-calendar':
        return <DesignCalendar />;
      case 'design-orders':
        return <DesignOrders />;
      
      // Reports & Analytics
      case 'reports':
        return <Reports />;
      case 'reports-dashboard':
        return <ReportsDashboard />;
      case 'ai-analytics':
        return <AIAnalyticsDashboard />;
      case 'report-project-status':
        return <ReportProjectStatus />;
      case 'report-sales-by-client':
        return <ReportSalesByClient />;
      case 'report-sales-by-type':
        return <ReportSalesByType />;
      case 'report-production-efficiency':
        return <ReportProductionEfficiency />;
      case 'report-permit-tracking':
        return <ReportPermitTracking />;
      case 'report-profit-salesperson':
        return <ReportProfitSalesperson />;
      
      // Forecasting
      case 'forecasting':
        return <Forecasting />;
      
      // Customer Orders
      case 'customer-orders':
        return <CustomerOrders />;
      
      // Sales Orders
      case 'sales-orders':
        return <SalesOrders />;
      
      // Purchase Orders
      case 'purchase-orders':
        return <PurchaseOrders />;
      
      // Projects
      case 'projects':
        return <Projects />;
      
      // PM Center
      case 'pm-assistant':
        return <PmAssistant />;
      
      // Jobs
      case 'jobs':
      case 'pm-jobs':
        return <Jobs onNavigate={setCurrentView} onJobSelect={setSelectedJob} />;
      case 'job-detail':
        return <JobDetail job={selectedJob} onNavigate={setCurrentView} />;
      case 'job-types':
        return <JobTypes />;
      
      // Clients
      case 'clients':
      case 'client-list':
        return <Clients />;
      case 'client-types':
        return <ClientTypes />;
      case 'client-detail':
        return <ClientDetail />;
      
      // Estimates
      case 'estimates':
      case 'estimates-main':
        return <Estimates />;
      case 'smart-sheets':
        return <SmartSheets onViewChange={setCurrentView} onSelectSheet={setSelectedJob} />;
      case 'smart-sheet-runner':
        return <SmartSheetRunner smartSheetId={selectedJob} onViewChange={setCurrentView} />;
      case 'smart-sheet-builder':
        return <SmartSheetBuilder id={selectedJob} onViewChange={setCurrentView} />;
      
      // Permits
      case 'permits':
      case 'permits-list':
        return <PermitList />;
      case 'permit-dashboard':
        return <PermitDashboard />;
      case 'permit-types':
        return <PermitTypes />;
      case 'permit-detail':
        return <PermitDetail />;
      case 'permit-new':
        return <PermitNew />;
      case 'permit-workflows':
        return <PermitWorkflows />;
      case 'autonomous-permitting':
      case 'permit-automation':
        return <AutonomousPermitDashboard />;
      case 'permit-settings':
        return <PermitSettingsPage />;
      case 'permit-agent':
        return <PermitAgentDashboard />;
      
      // Production
      case 'production':
        return <Production />;
      case 'production-jobs':
        return <ProductionJobs />;
      case 'production-job-detail':
        return <ProductionJobDetail />;
      case 'production-job-type':
        return <ProductionJobType />;
      case 'production-job-class':
        return <ProductionJobClass />;
      case 'production-no-charge-type':
        return <ProductionNoChargeType />;
      case 'production-split-jobs':
        return <ProductionSplitJobs />;
      case 'production-split-job-detail':
        return <ProductionSplitJobDetail />;
      case 'tm-review':
        return <TmReview />;
      case 'workflow-pm':
        return <WorkflowPM />;
      case 'workflow-production':
        return <WorkflowProduction />;
      case 'pm-workflows':
        return <PMWorkflows />;
      case 'ai-workflow-builder':
        return <AIWorkflowBuilder />;
      
      // CRM
      case 'crm':
        return <CRM />;
      case 'accounts':
        return <Accounts />;
      case 'contacts':
        return <Contacts />;
      case 'opportunities':
        return <Opportunities />;
      
      // Vendors
      case 'vendors':
      case 'vendor-list':
        return <Vendors />;
      case 'vendor-types':
        return <VendorTypes />;
      case 'vendor-detail':
        return <VendorDetail />;
      case 'vendor-map':
        return <VendorMap />;
      
      // Staff
      case 'staff':
        return <Staff />;
      case 'team-members':
        return <TeamMembers />;
      case 'departments':
        return <Departments />;
      case 'time-tracking':
        return <TimeTracking />;
      case 'staff-scheduler':
        return <StaffScheduler />;
      case 'staff-payout':
        return <StaffPayout />;
      case 'skills-services':
        return <SkillsServices />;

      // Staff Scheduling (AI Scheduler)
      case 'production-schedule':
        return <ProductionSchedule />;
      case 'install-schedule':
        return <InstallSchedule />;
      case 'schedule-proposals':
        return <ScheduleProposals />;
      
      // Tasks & Activities
      case 'tasks':
        return <Tasks />;
      case 'activities':
        return <Activities />;
      
      // Tickets & Support
      case 'tickets':
        return <Tickets />;
      case 'call-center':
        return <CallCenter />;
      
      // Documents
      case 'documents':
        return <Documents />;
      
      // Calendar
      case 'calendar':
        return <Calendar />;
      
      // Inventory
      case 'inventory':
        return <Inventory />;
      case 'inventory-catalog':
        return <InventoryCatalog />;
      case 'inventory-materials':
        return <InventoryMaterials />;
      case 'inventory-category':
        return <InventoryCategory />;
      case 'inventory-subcategory':
        return <InventorySubcategory />;
      case 'inventory-adjustment':
        return <InventoryAdjustment />;
      case 'inventory-audit':
        return <InventoryAudit />;
      case 'inventory-formula':
        return <InventoryFormula />;
      case 'inventory-services':
        return <InventoryServices />;
      
      // Installation
      case 'install-calendar':
        return <InstallCalendar />;
      case 'routes':
      case 'active-routes':
        return <ActiveRoutes />;
      case 'drivers':
        return <Drivers />;
      case 'vehicles':
        return <Vehicles />;
      
      // Shipping
      case 'shipping-receiving':
        return <ShippingReceiving />;
      case 'truck-load-optimizer':
        return <TruckLoadOptimizer />;
      
      // OHI Engine
      case 'ohi-dashboard':
        return <OHIDashboard />;
      case 'ohi-overview':
        return <OHIOverview />;
      case 'ohi-engine':
        return <OHIEngine />;
      case 'ohi-reports':
        return <OHIReports />;
      case 'ohi-alerts':
        return <OHIAlerts />;
      case 'ohi-audit':
        return <OHIAudit />;
      case 'ohi-command':
        return <OHICommand />;
      case 'ohi-divisions':
        return <OHIDivisions />;
      case 'ohi-departments':
        return <OHIDepartments />;
      case 'ohi-settings':
        return <OHISettings />;
      case 'ohi-teams':
        return <OHITeams />;
      case 'ohi-users':
        return <OHIUsers />;
      
      // Automations
      case 'automations':
        return <Automations />;
      
      // Role Management
      case 'role-management':
        return <RoleManagement />;
      case 'user-roles':
        return <UserRoles />;
      case 'client-user-roles':
        return <ClientUserRoles />;
      case 'approval-queue':
        return <ApprovalQueue />;
      
      // Workflows
      case 'workflow-service':
        return <WorkflowService />;
      case 'workflow-questionnaire':
        return <WorkflowQuestionnaire />;
      case 'workflow-custom-field':
        return <WorkflowCustomField />;
      case 'workflow-agreements':
        return <WorkflowAgreements />;
      
      // Purchasing
      case 'purchasing-vendors':
        return <PurchasingVendors />;
      case 'purchasing-proposals':
        return <PurchasingProposals />;
      
      // Misc Pages
      case 'shipping':
        return <Shipping />;
      case 'tax-categories':
        return <TaxCategories />;
      case 'n8n-setup':
        return <N8NSetup />;
      case 'ai-command-center':
        return <AICommandCenter />;
      case 'pickups':
        return <Pickups />;
      case 'monument-estimate':
        return <MonumentEstimate />;
      case 'install-template':
        return <InstallTemplate />;
      case 'drafting-plan':
        return <DraftingPlan />;
      case 'sign-schematic':
        return <SignSchematic />;
      case 'split-sales-orders':
        return <SplitSalesOrders />;
      case 'sales-order-pipeline':
        return <SalesOrderPipeline />;
      case 'production-dashboard':
        return <ProductionPipeline />;
      case 'install-dashboard':
        return <InstallDashboard />;
      case 'transportation':
        return <Transportation />;
      case 'task-queue':
        return <TaskQueue />;
      
      // World-Class PM Features
      case 'project-copilot':
        return <ProjectCopilot />;
      case 'pm-dashboard':
        return <PmDashboard />;
      case 'resource-planner':
        return <ResourcePlanner />;
      case 'analytics-dashboard':
        return <AnalyticsDashboard />;
      case 'collaboration-hub':
        return <CollaborationHub />;
      case 'document-generator':
        return <DocumentGenerator />;
      case 'client-portal':
        return <ClientPortal />;
      case 'workflow-automation':
        return <WorkflowAutomation />;
      case 'financial-intelligence':
        return <FinancialIntelligence />;
      case 'risk-dashboard':
        return <RiskDashboard />;
      case 'time-intelligence':
        return <TimeIntelligence />;
      case 'field-operations':
        return <FieldOperations />;
      
      // Settings
      case 'settings':
        return <Settings />;
      
      default:
        return <Dashboard />;
    }
  };

  return (
    <ErrorBoundary>
      <NotificationProvider>
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
          <Sidebar 
            currentView={currentView} 
            onViewChange={setCurrentView}
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <TopBar 
              onJobSelect={setSelectedJob} 
              onOpenNotifications={() => setNotificationsOpen(true)}
              onOpenSearch={() => setSearchOpen(true)}
            />
            <Box component="main" sx={{ flex: 1, overflow: 'auto' }} className="scrollbar-thin">
              <Suspense fallback={<PageLoader />}>
                <Fade in={true} timeout={300}>
                  <Box sx={{ height: '100%' }}>
                    {renderPage()}
                  </Box>
                </Fade>
              </Suspense>
            </Box>
          </Box>
          <GlobalAIChat />
          
          {/* Command Palette */}
          <CommandPalette 
            open={commandPaletteOpen}
            onClose={() => setCommandPaletteOpen(false)}
            onNavigate={setCurrentView}
          />
          
          {/* Global Search */}
          <GlobalSearchDialog
            open={searchOpen}
            onClose={() => setSearchOpen(false)}
            onNavigate={setCurrentView}
          />
          
          {/* Keyboard Shortcuts */}
          <KeyboardShortcutsDialog
            open={shortcutsOpen}
            onClose={() => setShortcutsOpen(false)}
          />
          
          {/* Notification Center */}
          <NotificationCenter
            open={notificationsOpen}
            onClose={() => setNotificationsOpen(false)}
            onNavigate={setCurrentView}
          />
        </Box>
      </NotificationProvider>
    </ErrorBoundary>
  );
}

export default App;
